"use strict";
/**
  * @reach/alert v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */


// src/reach-alert.tsx
import * as React from "react";
import * as ReactDOM from "react-dom";
import { VisuallyHidden } from "@reach/visually-hidden";
import { getOwnerDocument, useComposedRefs } from "@reach/utils";
var keys = {
  polite: -1,
  assertive: -1
};
var elements = {
  polite: {},
  assertive: {}
};
var liveRegions = {
  polite: null,
  assertive: null
};
var renderTimer;
var AlertRendered = React.forwardRef(function AlertRendered2({ as: Comp = "div", children, ...props }, forwardedRef) {
  return /* @__PURE__ */ React.createElement(Comp, {
    ref: forwardedRef,
    ...props
  }, children);
});
var Alert = React.forwardRef(function Alert2(props, forwardedRef) {
  let ownRef = React.useRef(null);
  let ref = useComposedRefs(forwardedRef, ownRef);
  useMirrorEffects(ownRef, props);
  let { type: _, ...rest } = props;
  return /* @__PURE__ */ React.createElement(AlertRendered, {
    ref,
    ...rest,
    "data-reach-alert": "",
    "aria-hidden": true
  });
});
Alert.displayName = "Alert";
function createMirror(type, doc) {
  let key = ++keys[type];
  let mount = (element) => {
    if (liveRegions[type]) {
      elements[type][key] = element;
      renderAlerts();
    } else {
      let node = doc.createElement("div");
      node.setAttribute(`data-reach-live-${type}`, "true");
      liveRegions[type] = node;
      doc.body.appendChild(liveRegions[type]);
      mount(element);
    }
  };
  let update = (element) => {
    elements[type][key] = element;
    renderAlerts();
  };
  let unmount = () => {
    delete elements[type][key];
    renderAlerts();
  };
  return { mount, update, unmount };
}
function renderAlerts() {
  if (renderTimer != null) {
    window.clearTimeout(renderTimer);
  }
  renderTimer = window.setTimeout(() => {
    Object.keys(elements).forEach((elementType) => {
      let regionType = elementType;
      let container = liveRegions[regionType];
      if (container) {
        ReactDOM.render(/* @__PURE__ */ React.createElement(VisuallyHidden, {
          as: "div"
        }, /* @__PURE__ */ React.createElement("div", {
          role: regionType === "assertive" ? "alert" : "status",
          "aria-live": regionType
        }, Object.keys(elements[regionType]).map((key) => React.cloneElement(elements[regionType][key], {
          key,
          ref: null
        })))), liveRegions[regionType]);
      }
    });
  }, 500);
}
function useMirrorEffects(ref, {
  type: regionType = "polite",
  ...props
}) {
  let mirror = React.useRef(null);
  React.useEffect(() => {
    let ownerDocument = getOwnerDocument(ref.current);
    if (!ownerDocument)
      return;
    mirror.current = createMirror(regionType, ownerDocument);
    mirror.current.mount(/* @__PURE__ */ React.createElement(AlertRendered, {
      ...props
    }));
    return () => {
      mirror.current?.unmount();
    };
  }, [ref, regionType, ...Object.values(props)]);
}
export {
  Alert
};
